import React from 'react';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Collapse,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
    Spinner,
} from 'reactstrap';
import styles from './ImageModal.module.css';
import {FocalPicker} from 'lib/react-image-focus/FocalPicker';
import {HwImage, Image, ImageActivity, ImageTypeEnum} from 'interfaces/models';
import FocalImageWrapper from './FocalImageWrapper';
import {SaveImageDataDto, UploadReplacementImageDto} from 'interfaces/dto';
import classNames from 'classnames';
import FileDropWrapper from '../../fileUpload/FileDropWrapper';
import CheckBox from '../../shared/CheckBox';

type ActDict = Record<number, { activityId: string, referenceKey: string, imageType: ImageTypeEnum, collapsed?: boolean }>

interface Props {
    isLoading: boolean,
    isUploadingFiles?: boolean,
    image: HwImage | Image,
    closeModal: () => void,
    navigateToNextImage: () => void,
    handleSave: (next: boolean, dto: SaveImageDataDto) => void,
    handleUploadReplacement?: (dto: UploadReplacementImageDto) => Promise<void>,
    
}

interface State {
    x: number,
    y: number,
    z: number,
    activities: ActDict,
    replacementToggle: boolean,
    replacementFile: File | null,
    replacementActivities: string[]
}

class ImageModal extends React.Component<Props, State> {
    readonly state: State = {
        x: 0,
        y: 0,
        z: 1,
        activities: {},
        replacementToggle: false,
        replacementFile: null,
        replacementActivities: []
    };

    componentDidMount() {
        this.populateState();
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps.image !== this.props.image 
            && (!prevProps.image || !this.props.image || prevProps.image.id !== this.props.image.id)) {
            this.populateState();
        }
    }

    populateState() {
        const {image} = this.props;
        if (image) {
            this.setState({
                x: image.focalPointX,
                y: image.focalPointY,
                z: image.focalPointZ
            });
            
            const hwImage = image as HwImage;
            if (hwImage.activities) {
                const activities = hwImage.activities.reduce((r: ActDict, e, i) => {
                    r[i] = e;
                    return r;
                }, {});
                this.setState({
                    activities
                });
            }
        }
    }

    renderActivities(image: HwImage) {
        const {replacementActivities} = this.state;
        const renderActivity = (activity: ImageActivity, idx: number) => {
            const stateActivity = this.state.activities[idx];
            if (!stateActivity) return null;

            const renderItem = (dt: string | JSX.Element, dd: string | JSX.Element) => {
                return <dl>
                    <dt>{dt}</dt>
                    <dd>{dd}</dd>
                </dl>;
            };

            const handleCollapse = () => {
                const activities = {...this.state.activities};
                const activity = {...stateActivity};
                activity.collapsed = !activity.collapsed;
                activities[idx] = activity;
                this.setState({activities});
            };
            
            const replaceIdx = () => replacementActivities?.indexOf(activity.activityId);
            
            const onReplaceChange = () => {
                const repIdx = replaceIdx();
                if (repIdx > -1) {
                    this.setState({replacementActivities: replacementActivities.filter(x => x !== activity.activityId)});
                } else {
                    this.setState({replacementActivities: [...replacementActivities, activity.activityId]});
                }
            }

            const renderCollapse = () => {
                return <span className={classNames('btn', styles.actCollapseBtn)} onClick={handleCollapse}>
                        <i className={!this.state.activities[idx].collapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-right'}/>
                    </span>
            };

            const renderTypeSelect = () => {
                const setImageType = (type: ImageTypeEnum) => {
                    const activities = {...this.state.activities};
                    const activity = {...stateActivity};
                    activity.imageType = type;
                    activities[idx] = activity;
                    this.setState({activities});
                };
                return <Form className={styles.imageTypeForm} onClick={e => e.stopPropagation()}>
                    <FormGroup check inline>
                        <Label check>
                            <Input type="radio" name="filterNeedsReview"
                                   checked={stateActivity.imageType === ImageTypeEnum.NotSet}
                                   onChange={() => setImageType(ImageTypeEnum.NotSet)}/>{' '}Unset
                        </Label>
                    </FormGroup>
                    <FormGroup check inline>
                        <Label check>
                            <Input type="radio" name="filterNeedsReview"
                                   checked={stateActivity.imageType === ImageTypeEnum.Critical}
                                   onChange={() => setImageType(ImageTypeEnum.Critical)}/>{' '}Critical
                        </Label>
                    </FormGroup>
                    <FormGroup check inline>
                        <Label check>
                            <Input type="radio" name="filterNeedsReview"
                                   checked={stateActivity.imageType === ImageTypeEnum.Supportive}
                                   onChange={() => setImageType(ImageTypeEnum.Supportive)}/>{' '}Supportive
                        </Label>
                    </FormGroup>
                </Form>
            }

            return <Card key={idx} className={"mt-3"}>
                <CardHeader onClick={handleCollapse}>
                    <Row>
                        <Col md={3}>
                            {renderItem('Activity', <a href={activity.activityHyperwaveUrl} target="_blank"
                                                       rel="noopener noreferrer">{activity.activityName}</a>)}
                        </Col>
                        <Col md={4}>
                            {renderItem('Image Type', renderTypeSelect())}
                        </Col>
                        {this.state.replacementToggle && <Col md={3}>
                            {renderItem('Replace Image', <CheckBox name={'replace' + idx} checked={replaceIdx() > -1} label="Select" onChange={onReplaceChange} />)}
                        </Col>}
                        <Col>
                            {renderCollapse()}
                        </Col>
                    </Row>
                </CardHeader>
                <Collapse isOpen={!this.state.activities[idx].collapsed}>
                    <CardBody className={styles.actCardBody}>
                        <iframe src={activity.activityCdnUrl} title={activity.activityId} referrerPolicy="no-referrer"
                                className={styles.activityFrame}/>
                    </CardBody>
                </Collapse>
            </Card>;
        }

        return <>
            <Row>
                <Col md={12}>
                    <h5>Set image type per activity</h5>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    {image.activities.map((a, i) => renderActivity(a, i))}
                </Col>
            </Row>
        </>;
    }

    renderImage(image: Image) {
        return <>
            <Row>
                <Col md={12}>
                    <h5>Set image focal point</h5>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={4}>
                    <div className={styles.focalPickerContainer}>
                        <FocalPicker src={image.originalUrl} x={image.focalPointX} y={image.focalPointY}
                                     onChange={(x, y) => this.setState({x, y})}/>
                    </div>
                    <div>
                        <input style={{width: "100%"}} type={"range"} min={1} max={5} step={0.01} value={this.state.z}
                               onChange={el => this.setState({z: +el.target.value})}/>
                    </div>
                    <span className="font-weight-light">Click on the image or drag the reticle to set focal point. Adjust the slider to zoom in</span>
                </Col>
                <Col md={8}>
                    <FocalImageWrapper image={image} x={this.state.x} y={this.state.y} z={this.state.z}/>
                </Col>
            </Row></>;
    }
    
    renderReplacementForm(image: HwImage) {
        const { replacementFile, replacementToggle, replacementActivities } = this.state;
        const { handleUploadReplacement } = this.props;
        const onFileInput = async (files: FileList) => {
            if (files.length)
                this.setState({replacementFile: files[0]});
        };
        
        const onUploadClick = async () => {
            if (!replacementFile || !handleUploadReplacement) return;
            
            await handleUploadReplacement({
                hwImageId: image.id,
                activityIds: replacementActivities,
                file: replacementFile
            });
        };
        
        const onCancelClick = () => {
            this.setState({
                replacementFile: null,
                replacementToggle: false
            })
        };
        
        const checkboxState = () => {
            if (!replacementActivities?.length)
                return false;
            if (replacementActivities.length === image.activities.length)
                return true;
            return undefined;
        }
        
        const onCheckboxChange = () => {
            const currentState = checkboxState();
            if (currentState) {
                this.setState({replacementActivities: []})
            } else {
                this.setState({replacementActivities: image.activities.map(a => a.activityId)});
            }
        }
        
        if (!replacementToggle) {
            return <>
                <Row className="mb-3">
                    <Col md={12}>
                        <Button color="secondary" onClick={() => this.setState({replacementToggle: true})}>Replace image</Button>
                    </Col>
                </Row>
            </>;
        }
        
        return <>
            <Row className="mb-1">
                <Col md={12}>
                    <h5>Upload replacement image</h5>
                </Col>
            </Row>
            <Row className="mb-3">
                <Col md={6}>
                    <FileDropWrapper isUploadingFiles={this.props.isUploadingFiles || false} onFileInput={onFileInput} />
                </Col>
                <Col md={4}>
                    <dl className="row">
                        {replacementFile && <>
                            <dt className="col-3">File:</dt>
                            <dd className="col-9">{replacementFile.name}</dd>
                        </>}
                        <dt className="col-3">Activities:</dt>
                        <dd className="col-9"><CheckBox label="Select all" checked={checkboxState()} name="selectActivities" onChange={(e) => onCheckboxChange()} /></dd>
                    </dl>
                </Col>
                <Col md={2}>
                    <Button color="primary" onClick={onUploadClick} disabled={!replacementFile || !replacementActivities?.length}>Upload</Button>&nbsp;
                    <Button color="secondary" onClick={onCancelClick}>Cancel</Button>
                </Col>
            </Row>
        </>;
    }

    render() {
        const {image, navigateToNextImage, closeModal, isLoading, handleSave} = this.props;
        const hwImage = image as HwImage;
        
        const onSave = (next: boolean) => {
            const {x, y, z, activities} = this.state;
            const dto: SaveImageDataDto = {
                image: {
                    imageId: image.id,
                    focalPointX: x,
                    focalPointY: y,
                    focalPointZ: z,
                },
                activities: activities ? Object.values(activities) : undefined
            };
            handleSave(next, dto);
        }

        return <Modal isOpen className={styles.imageModal} backdrop toggle={closeModal}>
            <ModalHeader toggle={closeModal}>{image.fileName}</ModalHeader>
            <ModalBody>
                <Container fluid>
                    {isLoading ? <div style={{textAlign: "center"}}><Spinner style={{width: '3rem', height: '3rem'}}
                                                                             type="grow"/></div> : <>
                        {hwImage.activities && this.renderReplacementForm(hwImage)}
                        {this.renderImage(image)}
                        {hwImage.activities && this.renderActivities(hwImage)}
                    </>}
                </Container>
            </ModalBody>
            <ModalFooter>
                {!isLoading && <Container fluid>
                    <Row>
                        <Col md={2} className={"offset-md-4"}>
                            <Button outline={true} color="secondary" onClick={closeModal} size="sm" block>Cancel &
                                Close</Button>
                        </Col>
                        <Col md={2}>
                            <Button outline={true} color="secondary" onClick={navigateToNextImage} size="sm" block>Cancel
                                &
                                Next</Button>
                        </Col>
                    </Row>
                    <Row className={"mt-5"}>
                        <Col md={3} className="offset-md-3">
                            <Button color="secondary" onClick={() => onSave(false)} size="lg" block>Save &
                                Close</Button>
                        </Col>
                        <Col md={3} className="">
                            <Button color="primary" onClick={() => onSave(true)} size="lg" block>Save &
                                Next</Button>
                        </Col>
                    </Row>
                </Container>}
            </ModalFooter>
        </Modal>;
    }
}

export default ImageModal;